import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Button, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import HeaderNotificationButton from '../HeaderNotificationButton';
import PortalColorModeToggle from './PortalColorModeToggle';
import { useAuth } from '../../contexts/AuthContext';
import { usePortalHeaderLeading } from './PortalPageHeader';
import { usePortalTokens } from '../../styles/usePortalTokens';

const PortalLayoutHeader = ({
  user,
  notificationsPath,
  onSignOut,
  signOutRedirectPath,
  onMenuOpen,
}) => {
  const navigate = useNavigate();
  const { signingOut } = useAuth();
  const leading = usePortalHeaderLeading();
  const [anchorEl, setAnchorEl] = useState(null);
  const portal = usePortalTokens();
  const userInitial = (user.name || 'A').charAt(0).toUpperCase();
  const avatarSrc = String(user.avatarUrl ?? '').trim() || undefined;
  const avatarFallbackSx = {
    bgcolor: portal.inputBorder,
    color: portal.textMuted,
    fontWeight: 700,
    border: `1px solid ${portal.border}`,
  };

  const handleSignOutClick = async () => {
    if (signingOut) {
      return;
    }
    setAnchorEl(null);
    await onSignOut();
    navigate(signOutRedirectPath);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        px: { xs: 2, sm: 3, md: 4 },
        pt: { xs: 2, md: 3.25 },
        pb: 2.75,
        gap: 1.5,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        <IconButton
          onClick={onMenuOpen}
          aria-label="Open navigation menu"
          sx={{ display: { lg: 'none' }, flexShrink: 0, color: portal.textMuted }}
        >
          <MenuIcon />
        </IconButton>
        {leading?.showBack ? (
          <Typography
            component="button"
            type="button"
            onClick={leading.onBack}
            sx={{
              border: 0,
              background: 'none',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 700,
              color: portal.accent,
              p: 0,
              mr: 0.75,
              fontFamily: 'inherit',
              flexShrink: 0,
            }}
          >
            ‹ Back
          </Typography>
        ) : null}
        {leading?.title ? (
          <Typography
            sx={{
              fontSize: { xs: 18, md: 20 },
              fontWeight: 800,
              color: portal.text,
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {leading.title}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 1.25 }}>
        <PortalColorModeToggle />
        <HeaderNotificationButton notificationsPath={notificationsPath} />
        <Tooltip
          title={
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: 150,
                maxWidth: 240,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  mb: 0.5,
                  textAlign: 'center',
                  width: '100%',
                  overflowWrap: 'anywhere',
                }}
              >
                {user.name}
              </Typography>
              <Typography variant="body2" sx={{ textAlign: 'center', overflowWrap: 'anywhere' }}>
                {user.email}
              </Typography>
            </Box>
          }
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                maxWidth: 260,
                '& .MuiTooltip-arrow': {
                  color: 'rgba(0, 0, 0, 0.8)',
                },
              },
            },
          }}
          placement="bottom-end"
          arrow
        >
          <IconButton
            onClick={(event) => setAnchorEl(event.currentTarget)}
            aria-label="Account menu"
            sx={{ p: 0 }}
          >
            <Avatar
              src={avatarSrc}
              alt={user.name}
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                ...avatarFallbackSx,
              }}
            >
              {userInitial}
            </Avatar>
          </IconButton>
        </Tooltip>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: 'action.hover',
              p: 2,
              maxWidth: 'calc(100vw - 24px)',
            },
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 250,
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <Typography
              variant="body2"
              sx={{ mb: 2, textAlign: 'center', width: '100%', overflowWrap: 'anywhere' }}
            >
              {user.email}
            </Typography>
            <Avatar
              src={avatarSrc}
              alt={user.name}
              sx={{ width: 80, height: 80, mb: 2, fontSize: 28, ...avatarFallbackSx }}
            >
              {userInitial}
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                textAlign: 'center',
                width: '100%',
                minWidth: 0,
                lineHeight: 1.3,
                overflowWrap: 'anywhere',
              }}
            >
              Hi, {user.name}!
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<ExitToAppIcon />}
              disabled={signingOut}
              sx={{
                textTransform: 'none',
                color: portal.accent,
                borderColor: portal.text,
                '&:hover': { borderColor: portal.text, bgcolor: 'transparent' },
              }}
              onClick={handleSignOutClick}
            >
              {signingOut ? 'Signing out…' : 'Sign out'}
            </Button>
          </Box>
        </Popover>
      </Box>
    </Box>
  );
};

export default PortalLayoutHeader;
